import { Api, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import {
    Button, Checkbox, DataSourceMode, Label, LayoutProvidedCrudDecoratorProps, Snackbar, Table, TableRow,
    TableRowDisplayEvent
} from "@mcleod/components";
import { AddStopCompareItems } from "./AddStopCompareItems";
// import { DeleteStopCompareItems } from "./DeleteStopCompareItems";
import { AutogenLayoutCompareOrder } from "./autogen/AutogenLayoutCompareOrder";
import { ChangeStopCompareItems } from "./ChangeStopCompareItems";
import { DeleteStopCompareItems } from "./DeleteStopCompareItems";
import { OtherChargeChangeCompareItem } from "./OtherChargeChangeCompareItem";
import { OtherChargeCompare } from "./OtherChargeCompare";
import { HandlingRequirementsCompare } from "./HandlingRequirementsCompare";
import { RowEdiCompareItem } from "./models/ModelEdiCompareItem";
import { HandlingRequirementsCompareChangeItem } from "./HandlingRequirementsCompareChangeItem";
import { FreightGroupItemCompare } from "./FreightGroupItemCompare";
import { FreightGroupItemCompareChangeItem } from "./FreightGroupItemCompareChangeItem";
import { ExtraPayCompare } from "./ExtraPayCompare";
import { EdiUtility } from "./EdiUtility";
import { ExtraPayChangeCompareItem } from "./ExtraPayChangeCompareItem";
import { ChangeStopRefnoNoChangeCompareItems } from "./ChangeStopRefnoNoChangeCompareItems";

const log = LogManager.getLogger("datafusion.CompareOrder");

// Constants for component management
const LABEL_IDS_TO_HIDE = {
    ADD_STOP: [
        "labelAddStopLocation", "labelAddStopAddress", "labelAddStopCity", "labelAddStopState",
        "labelAddStopZip", "labelAddStopComma", "horizontalspacer3", "labelCheckedAddStop", "checkboxAddStopApply"
    ],
    REMOVE_STOP: [
        "labelRemovedStopLocation", "labelRemovedStopAddress", "labelRemovedStopCity", "labelRemovedStopState",
        "labelRemovedStopZip", "labelRemovedStopComma", "horizontalspacer4", "labelCheckedRemovedStops", "checkboxRemoveStopApply"
    ],
    STOP_CHANGE: [
        "labelStopChange", "labelStopChangeLocationName", "labelStopChangeAddress", "labelStopChangeCity",
        "labelStopChangeState", "labelStopChangeZip", "labelStopChangeComma"
    ]
};

const GROUP_TYPE_FILTERS = {
    REFNO: ["NR", "OR"],
    REFNO_CHANGED: ["RC"],
    STOP_NOTES: ["NN", "ON"]
};

export class CompareOrder extends AutogenLayoutCompareOrder {
    public compareData: ModelRow[];
    public stopChanges: ModelRow[];
    public newStops: ModelRow[];
    public removedStops: ModelRow[];
    public newOrRemovedOtherCharges: ModelRow[];
    public newOrRemovedExtraPay: ModelRow[];
    public changedOtherCharges: ModelRow[];
    public changedExtraPay: ModelRow[];
    public changedAccessorials: ModelRow[];
    public newOrRemovedAccessorials: ModelRow[];
    public changedFreightGroupItems: ModelRow[];
    public newOrRemovedFreightGroupItems: ModelRow[];
    public orderChangeGroupItems: ModelRow[];
    public movementChangeGroupItems: ModelRow[];
    private _selectAll: boolean;
    private dataRows: ModelRow[];
    private mode: DataSourceMode;
    public orderId: String;
    public tenderId: String;
    public invoiceId: String = null;
    public moveId: String = null;
    public isInvoice: boolean = false;
    private _buttonApply: Button;
    private _changesApplied: number = 0;
    private _isCompareHistory: boolean = false;
    private _isOrderUpdateAllowed: boolean = false;
    private _isSelectAllVisible: boolean = true;
    private _isInvoiceOrderUpdateAllowed: boolean = false;
    private _isInvoiceMovementUpdateAllowed: boolean = false;
    private _isInvoiceStopUpdateAllowed: boolean = false;

    public get changesApplied(): number {
        return this._changesApplied;
    }
    public set changesApplied(value: number) {
        this._changesApplied = value;
    }

    public get isOrderUpdateAllowed(): boolean {
        return this._isOrderUpdateAllowed;
    }

    public set isOrderUpdateAllowed(value: boolean) {
        this._isOrderUpdateAllowed = value;
    }

    override onLoad(): void | Promise<void> {
        if (!this.isCompareHistory) {
            this.setCompareTables();
        }
    }

    public override get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return { customDataLoader: this.loadFromApiData };
    }

    public loadFromApiData(apiData: any, mode: DataSourceMode) {
        this.setCompareVariables(apiData, mode);
    }

    public loadFromApiDataOrderHistory(apiData: any, mode: DataSourceMode) {
        this.isCompareHistory = true;
        this.layoutOtherChargeCompare.isCompareHistory = true;
        this.layoutExtraPayCompare.isCompareHistory = true;
        this.layoutHandlingRequirementChange.isCompareHistory = true;
        this.layoutFreightGroupItemChange.isCompareHistory = true;
        this.setCompareVariables(apiData, mode);
        this.setCompareTables();
    }

    setOrderChanges(orderChanges: ModelRow[], mode: DataSourceMode) {
        if (orderChanges != null && orderChanges?.length > 0) {
            const compareItems = orderChanges[0]["compare_items"];
            if (this.isInvoice) {
                this.isInvoiceOrderUpdateAllowed = this.checkInvoiceOrderUpdateAllowed(compareItems);
            }
            this.loadChildTableData(this.tableOrderChanges, compareItems, mode);
            this.tabOrderChanges.caption = "Order Changes " + compareItems?.length;
            this.tabOrderChanges.visible = true;
        } else {
            this.tabOrderChanges.visible = false;
        }
    }
    setMovementChanges(movementChanges: ModelRow[], mode: DataSourceMode) {
        if (movementChanges != null && movementChanges?.length > 0) {
            const compareItems = movementChanges[0]["compare_items"];
            if (this.isInvoice) {
                this.isInvoiceMovementUpdateAllowed = this.checkInvoiceOrderUpdateAllowed(compareItems);
            }
            this.loadChildTableData(this.tableMovementChanges, compareItems, mode);
            this.tabMovement.caption = "Movement Changes " + compareItems?.length;
            this.tabMovement.visible = true;
        } else {
            this.tabMovement.visible = false;
        }
    }

    setStopChanges(stopChanges: ModelRow[], mode: DataSourceMode) {
        if (stopChanges != null && stopChanges?.length > 0) {
            this.loadChildTableData(this.tableStopChanges, stopChanges, mode);
            // const stopChangeLayout = this.tableStopChanges.rows[0].findComponentById("layoutChangeStopCompare") as ChangeStopCompareItems;
            this.tabStopChanges.caption = "Stop Changes " + stopChanges?.length;
            this.tabStopChanges.visible = true;
        } else {
            this.tabStopChanges.visible = false;
        }
    }

    setNewStops(newStops: ModelRow[], mode: DataSourceMode) {
        if (newStops == null || newStops?.length == 0) {
            this.tabNewStops.visible = false;
            this.panelStopAdd.visible = false;
        } else {
            this.loadChildTableData(this.tableStopAdd, newStops, mode);
            this.tabNewStops.caption = "New Stops " + newStops?.length;
            this.tabNewStops.visible = true;
            this.panelStopAdd.visible = true;
        }
    }

    setRemovedStops(delStops: ModelRow[], mode: DataSourceMode) {
        if (delStops == null || delStops?.length == 0) {
            this.tabRemovedStops.visible = false;

        } else {
            this.loadChildTableData(this.tableStopRemove, delStops, mode);
            this.tabRemovedStops.caption = "Removed Stops " + delStops?.length;
            this.tabRemovedStops.visible = true;
        }
    }

    setOtherCharges(mode: DataSourceMode) {
        this.setupCompareSection({
            newOrRemovedItems: this.newOrRemovedOtherCharges,
            changedItems: this.changedOtherCharges,
            tab: this.tabOtherCharges,
            layoutId: "layoutOtherChargeCompare",
            tabCaption: "Other Charges",
            mode,
            setupCallback: (compare: OtherChargeCompare) => {
                compare.newOrRemovedOtherCharges = this.newOrRemovedOtherCharges;
                compare.changedOtherCharges = this.changedOtherCharges;
            }
        });
    }

    setExtraPay(mode: DataSourceMode) {
        this.setupCompareSection({
            newOrRemovedItems: this.newOrRemovedExtraPay,
            changedItems: this.changedExtraPay,
            tab: this.tabExtraPay,
            layoutId: "layoutExtraPayCompare",
            tabCaption: "Extra Pay",
            mode,
            setupCallback: (compare: ExtraPayCompare) => {
                compare.newOrRemovedExtraPay = this.newOrRemovedExtraPay;
                compare.changedExtraPay = this.changedExtraPay;
            }
        });
    }

    setHandlingRequirements(mode: DataSourceMode) {
        this.setupCompareSection({
            newOrRemovedItems: this.newOrRemovedAccessorials,
            changedItems: this.changedAccessorials,
            tab: this.tabAccessorials,
            layoutId: "layoutHandlingRequirementChange",
            tabCaption: "Accessorials Changes",
            mode,
            setupCallback: (compare: HandlingRequirementsCompare) => {
                compare.newOrRemovedHandlingRequirements = this.newOrRemovedAccessorials;
                compare.changedHandlingRequirements = this.changedAccessorials;
            }
        });
    }

    setFreightGroupItems(mode: DataSourceMode) {
        this.setupCompareSection({
            newOrRemovedItems: this.newOrRemovedFreightGroupItems,
            changedItems: this.changedFreightGroupItems,
            tab: this.tabFreightGroupItems,
            layoutId: "layoutFreightGroupItemChange",
            tabCaption: "Freight Item Changes",
            mode,
            setupCallback: (compare: FreightGroupItemCompare) => {
                compare.newOrRemovedFreightGroupItems = this.newOrRemovedFreightGroupItems;
                compare.changedFreightGroupItems = this.changedFreightGroupItems;
            }
        });
    }

    private setupCompareSection<T extends { isOrderUpdateAllowed: boolean; isInvoice: boolean; setupTableData: (mode: DataSourceMode) => void }>(options: {
        newOrRemovedItems: ModelRow[];
        changedItems: ModelRow[];
        tab: any;
        layoutId: string;
        tabCaption: string;
        mode: DataSourceMode;
        setupCallback: (compare: T) => void;
    }) {
        const hasNewOrRemovedItems = options.newOrRemovedItems?.length > 0;
        const hasChangedItems = options.changedItems?.length > 0;

        if (!hasNewOrRemovedItems && !hasChangedItems) {
            options.tab.visible = false;
            return;
        }

        const totalItems = (options.newOrRemovedItems?.length || 0) + (options.changedItems?.length || 0);
        options.tab.caption = `${options.tabCaption} ${totalItems}`;

        const compare = this.findComponentById(options.layoutId) as unknown as T;
        options.setupCallback(compare);
        compare.isOrderUpdateAllowed = this.isOrderUpdateAllowed;
        compare.isInvoice = this.isInvoice;
        compare.setupTableData(options.mode);
        options.tab.visible = true;
    }

    onOrderChangeRowDisplay(event: TableRowDisplayEvent) {
        this.setupRowDisplayBase(event, {
            checkboxId: "checkboxApplyOrder",
            labelId: "labelCheckedOrder",
            errorLabelId: "labelApplyErrorOrder",
            allowedCondition: () => (this.isInvoice && this.isInvoiceOrderUpdateAllowed) || this.isOrderUpdateAllowed
        });
    }

    /** This is an event handler for the onRowDisplay event of tableMovementChanges. */
    tableMovementChangesOnRowDisplay(event: TableRowDisplayEvent) {
        this.setupRowDisplayBase(event, {
            checkboxId: "checkboxApplyMovement",
            labelId: "labelCheckedMovement",
            errorLabelId: "labelApplyErrorMovement",
            allowedCondition: () => (this.isInvoice && this.isInvoiceMovementUpdateAllowed) || this.isOrderUpdateAllowed
        });
    }

    private setupRowDisplayBase(event: TableRowDisplayEvent, config: {
        checkboxId: string;
        labelId: string;
        errorLabelId: string;
        allowedCondition: () => boolean;
    }) {
        const tableRow = event.getTableRow();
        const modelRow: ModelRow = event.target.data;
        const checkbox = tableRow.findComponentById(config.checkboxId) as Checkbox;
        const labelChecked = tableRow.findComponentById(config.labelId) as Label;

        if (!this.isCompareHistory) {
            if (config.allowedCondition()) {
                checkbox.printable = false;
                checkbox.addClickListener(() => labelChecked.visible = checkbox.checked);
            } else {
                checkbox.visible = false;
            }
        } else {
            const labelApplyError = tableRow.findComponentById(config.errorLabelId) as Label;
            checkbox.visible = false;
            labelApplyError.visible = true;
            const apply = modelRow.get("apply");

            if (!apply) {
                labelApplyError.setProps(EdiUtility.getErrorProps());
            } else {
                labelApplyError.setProps(EdiUtility.getSuccessProps());
            }
        }
    }

    onStopAddDisplayRow(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow: ModelRow = event.target.data;
        const checkbox = tableRow.findComponentById("checkboxAddStopApply") as Checkbox;
        const labelChecked = tableRow.findComponentById("labelCheckedAddStop") as Label;

        this.setCheckboxVisibility(checkbox, labelChecked, () => ((this.isInvoice && this.newStops[0]["compare_items"].some((item: any) => item["apply"] === true)) || (!this.isCompareHistory && this.isOrderUpdateAllowed)));

        this.setupStopCompareItems(tableRow, this.newStops, "source_value", AddStopCompareItems);
        this.handleCompareHistoryDisplay(tableRow, modelRow, "labelAddStopApply", "labelGroupNameAddStop", LABEL_IDS_TO_HIDE.ADD_STOP);
    }

    onStopDeleteDisplayRow(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow: ModelRow = event.target.data;
        const checkbox = tableRow.findComponentById("checkboxRemoveStopApply") as Checkbox;
        const labelChecked = tableRow.findComponentById("labelCheckedRemovedStops") as Label;

        this.setCheckboxVisibility(checkbox, labelChecked, () => ((this.isInvoice && this.removedStops[0]["compare_items"].some((item: any) => item["apply"] === true)) || (!this.isCompareHistory && this.isOrderUpdateAllowed)));

        this.setupStopCompareItems(tableRow, this.removedStops, "target_value", DeleteStopCompareItems);
        this.handleCompareHistoryDisplay(tableRow, modelRow, "labelRemoveStopApply", "labelGroupNameRemoveStop", LABEL_IDS_TO_HIDE.REMOVE_STOP);
    }

    private setupStopCompareItems(tableRow: TableRow, dataSource: ModelRow[], valueField: "source_value" | "target_value", ComponentType: any) {
        tableRow.findComponentById((component: any) => {
            if (component instanceof ComponentType) {
                const compareItems = component;
                const rows = [];
                dataSource[tableRow.index]["compare_items"].forEach((element: any) => {
                    const newRow = new RowEdiCompareItem(element);
                    if (!StringUtil.isEmptyString(newRow.get(valueField)) && newRow.get(valueField) !== "1900-01-01") {
                        rows.push(newRow);
                    }
                });
                compareItems.setupTableData(rows);
            }
        });
    }

    private handleCompareHistoryDisplay(tableRow: TableRow, modelRow: ModelRow, applyLabelId: string, groupNameLabelId: string, labelsToHide: string[]) {
        if (!this.isCompareHistory) return;

        tableRow.findComponentById((component: any) => {
            if (component.id === applyLabelId) {
                const compareItems: any[] = modelRow.get("compare_items");
                const hasAppliedItems = compareItems.some((item) => item["apply"] === true);
                component.setProps(hasAppliedItems ? EdiUtility.getSuccessProps() : EdiUtility.getErrorProps());
                component.visible = true;
            } else if (component.id === groupNameLabelId) {
                component.visible = true;
            } else if (labelsToHide.includes(component.id)) {
                component.visible = false;
            }
        });
    }

    onStopChangeDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();

        this.setupChangeStopCompareItems(tableRow);
        this.handleStopChangeVisibility(tableRow);
        this.setAlternatingRowColors(tableRow, event.target);
    }

    private setupChangeStopCompareItems(tableRow: TableRow) {
        let isInvoiceOrderUpdateAllowed = false;
        tableRow.findComponentById((component: any) => {
            if (component instanceof ChangeStopCompareItems) {
                const compareItems: ChangeStopCompareItems = component;
                const rows = this.stopChanges[tableRow.index]["compare_items"].map((element: any) =>
                    new RowEdiCompareItem(element)
                );
                if (this.isInvoice) {
                    isInvoiceOrderUpdateAllowed = this.checkInvoiceOrderUpdateAllowed(this.stopChanges[tableRow.index]["compare_items"]);
                }

                this.setupChildItemGroups(tableRow, compareItems);
                compareItems.isOrderUpdateAllowed = (this.isInvoice && isInvoiceOrderUpdateAllowed) || (this.isOrderUpdateAllowed);
                compareItems.setupTableData(rows, this._isCompareHistory);
            }
        });
    }

    private setupChildItemGroups(tableRow: TableRow, compareItems: ChangeStopCompareItems) {
        const childItemGroups = this.stopChanges[tableRow.index]["child_item_groups"];
        if (!childItemGroups) return;

        const refNos = childItemGroups.filter(rowData => GROUP_TYPE_FILTERS.REFNO.includes(rowData["group_type"]));
        const refNosChanged = childItemGroups.filter(rowData => GROUP_TYPE_FILTERS.REFNO_CHANGED.includes(rowData["group_type"]));
        const stopNotes = childItemGroups.filter(rowData => GROUP_TYPE_FILTERS.STOP_NOTES.includes(rowData["group_type"]));

        compareItems.refnos = refNos;
        compareItems.refNosChanged = refNosChanged;
        compareItems.stopNotes = stopNotes;
    }

    private handleStopChangeVisibility(tableRow: TableRow) {
        tableRow.findComponentById((component: any) => {
            if (!this.isCompareHistory && component.id === "labelStopGroupName") {
                component.visible = false;
            } else if (this.isCompareHistory && LABEL_IDS_TO_HIDE.STOP_CHANGE.includes(component.id)) {
                component.visible = false;
            }
        });
    }

    private setAlternatingRowColors(tableRow: TableRow, target: any) {
        target.backgroundColor = tableRow.index % 2 === 0 ? "#FFFFFF" : "subtle.lightest";
    }

    private loadChildTableData(table: Table, data: any[], mode: DataSourceMode) {
        const tableDataSource = table.dataSource;
        tableDataSource.data = [];
        const modelRows = [];
        data.forEach((element: any) => {
            modelRows.push(new ModelRow(tableDataSource.url, true, element));
        });
        tableDataSource.setRowsAndMode(mode, modelRows);
    }

    private _getArrayAndNull(row: ModelRow, field: string): ModelRow[] {
        const result = row.get(field) || [];
        row.set(field, null);
        return result;
    }

    selectAllOnClick() {
        this._selectAll = true;
        this.processSelectAllTables();
    }
    onDeselectAll() {
        this._selectAll = false;
        this.processSelectAllTables();
    }
    processSelectAllTables() {
        this.tableSetSelected(this.tableOrderChanges, this._selectAll);
        this.tableSetSelected(this.tableStopAdd, this._selectAll);
        this.tableSetSelected(this.tableStopRemove, this._selectAll);
        this.tableSetSelected(this.tableMovementChanges, this._selectAll);
        this.tableStopChanges.rows.forEach(tableRow => {
            tableRow.findComponentById((comp: any) => {
                if (comp instanceof ChangeStopCompareItems) {
                    this.tableSetSelected(comp.tableCompareItems, this._selectAll);
                    this.tableSetSelected(comp.tableStopComment, this._selectAll);
                    this.tableSetSelected(comp.tableStopRefNo, this._selectAll);
                }
            });
        });
        this.setLayoutValues(this._selectAll);
    }

    tableSetSelected(table: Table, selected: boolean) {
        table.rows.forEach(tableRow => {
            const { checkBoxValue, labelValue } = this.getCorrectComponentValues(table);
            const check = tableRow.findComponentById(checkBoxValue) as Checkbox
            const label = tableRow.findComponentById(labelValue) as Label;
            if (check && check.visible) {
                if (selected) {
                    tableRow.data.set("apply", "Y");
                    label.visible = true;
                } else {
                    tableRow.data.set("apply", "N");
                    label.visible = false;
                }
                check.checked = selected;
            } else {
                tableRow.data.set("apply", "N");
            }
        });
    }

    public applyChanges(buttonApplyChanges: Button): number {

        const orderChanges: ModelRow[] = [];
        const stopChanges: ModelRow[] = [];
        const stopChangeGroups: ModelRow[] = [];
        const refnoChanges: ModelRow[] = [];
        const refnoAddDelete: ModelRow[] = [];
        const commentChanges: ModelRow[] = [];
        const ocAddsDeletes: ModelRow[] = [];
        const ocChanges: ModelRow[] = [];
        const handlingRequirementsAddsDeletes: ModelRow[] = [];
        const handlingRequirementsChanges: ModelRow[] = [];
        const freightGroupItemAddsDeletes: ModelRow[] = [];
        const freightGroupItemChanges: ModelRow[] = [];
        const extraPayAddDeletes: ModelRow[] = [];
        const extraPayChanges: ModelRow[] = [];
        const movementChanges: ModelRow[] = [];

        this.changesApplied += this.processCompareTable(this.tableOrderChanges, orderChanges);
        this.changesApplied += this.processCompareTable(this.tableStopChanges, stopChangeGroups);
        this.changesApplied += this.processCompareTable(this.tableMovementChanges, movementChanges);

        this.tableStopChanges.rows.forEach(tableRow => {
            tableRow.findComponentById((comp: any) => {
                if (comp instanceof ChangeStopCompareItems) {
                    this.changesApplied += this.processCompareTable(comp.tableCompareItems, stopChanges);
                    this.changesApplied += this.processCompareTable(comp.tableStopComment, commentChanges);
                    this.changesApplied += this.processCompareTable(comp.tableStopRefNo, refnoAddDelete);
                    comp.tableRefNoCompareChanges.rows.forEach((row, index) => {
                        refnoChanges.push(row.data);
                        row.findComponentById((component: any) => {
                            if (component instanceof ChangeStopRefnoNoChangeCompareItems) {
                                this.changesApplied += this.processNestedCompareTable(
                                    component.tableRefnoChanges,
                                    refnoChanges,
                                    index
                                );
                            }
                        });
                    });
                }
            });
        });
        //new stops
        const stopAdds: ModelRow[] = [];
        this.changesApplied += this.processCompareTable(this.tableStopAdd, stopAdds);
        //deleted stops
        const stopDels: ModelRow[] = [];
        this.changesApplied += this.processCompareTable(this.tableStopRemove, stopDels);

        //other charge changes/adds/deletes
        const ocCompare = this.findComponentById("layoutOtherChargeCompare") as OtherChargeCompare;
        this.changesApplied += this.processOtherChargeCompareTable(ocCompare.tableOtherCharge, ocAddsDeletes);
        ocCompare.tableOCChanges.rows.forEach((tableRow, index) => {
            ocChanges.push(tableRow.data);
            tableRow.findComponentById((comp: any) => {
                if (comp instanceof OtherChargeChangeCompareItem) {
                    this.changesApplied += this.processNestedCompareTable(
                        comp.tableChangeCompareItems,
                        ocChanges,
                        index
                    );
                }
            });
        });

        //handling requirements changes/adds/deletes
        const handlingRequirementsCompare = this.findComponentById("layoutHandlingRequirementChange") as HandlingRequirementsCompare;
        this.changesApplied += this.processCompareTable(handlingRequirementsCompare.tableHandlingRequirementAdd, handlingRequirementsAddsDeletes);
        handlingRequirementsCompare.tableHandlingRequirementChange.rows.forEach(tableRow => {
            tableRow.findComponentById((comp: any) => {
                if (comp instanceof HandlingRequirementsCompareChangeItem) {
                    this.changesApplied += this.processCompareTable(comp.tableHandlingChangeItems, handlingRequirementsChanges);
                }
            })
        });

        const freightGroupCompare = this.findComponentById("layoutFreightGroupItemChange") as FreightGroupItemCompare;
        this.changesApplied += this.processCompareTable(freightGroupCompare.tableFreightGroupItemAdd, freightGroupItemAddsDeletes);
        freightGroupCompare.tableFreightGroupItemChange.rows.forEach(tableRow => {
            tableRow.findComponentById((comp: any) => {
                if (comp instanceof FreightGroupItemCompareChangeItem) {
                    this.changesApplied += this.processCompareTable(comp.tableFreightGroupItemChangeItems, freightGroupItemChanges);
                }
            })
        });

        const extraPayCompare = this.findComponentById("layoutExtraPayCompare") as ExtraPayCompare;
        this.changesApplied += this.processCompareTable(extraPayCompare.tableDriverExtraPay, extraPayAddDeletes);
        extraPayCompare.tableEPChanges.rows.forEach((tableRow, index) => {
            extraPayChanges.push(tableRow.data);
            tableRow.findComponentById((comp: any) => {
                if (comp instanceof ExtraPayChangeCompareItem) {
                    this.changesApplied += this.processNestedCompareTable(
                        comp.tableChangeCompareItems,
                        extraPayChanges,
                        index
                    );
                }
            });
        })

        Api.post("lme/datafusion/process-compare", {
            order_id: this.orderId,
            edi_order_id: this.tenderId,
            invoice_id: this.invoiceId ?? null,
            move_id: this.moveId ?? null,
            order_changes: orderChanges,
            stop_changes: stopChanges,
            stop_change_groups: stopChangeGroups,
            stop_adds: stopAdds,
            stop_removals: stopDels,
            refno_changes: refnoChanges,
            refno_adds_deletes: refnoAddDelete,
            comment_changes: commentChanges,
            othercharge_adds_deletes: ocAddsDeletes,
            othercharge_changes: ocChanges,
            handling_reqs_adds_deletes: handlingRequirementsAddsDeletes,
            handling_reqs_changes: handlingRequirementsChanges,
            freight_group_adds_deletes: freightGroupItemAddsDeletes,
            freight_group_changes: freightGroupItemChanges,
            movement_changes: movementChanges,
            extra_pay_add_deletes: extraPayAddDeletes,
            extra_pay_changes: extraPayChanges
        }).then(async res => {
            buttonApplyChanges.busy = false;
            if (!StringUtil.isEmptyString(res.data[0].processing_error)) {
                Snackbar.showWarningSnackbar(res.data[0].processing_error);
                this.changesApplied = 0;
            } else {
                this.slideOut();
            }
        }).catch(reason => {
            log.debug(reason);
            Snackbar.showWarningSnackbar("Unable to process change tender : " + reason);
            this.buttonApply.busy = false;
        });
        return this.changesApplied;
    }

    public processCompareTable(table: Table, compareRows: ModelRow[]): number {
        let count: number = 0;
        table.rows.forEach(tableRow => {
            compareRows.push(tableRow.data);
            if ("Y" == tableRow.data.get("apply")) {
                count++;
            }
        });
        return count;
    }

    private processNestedCompareTable(table: Table, parentArray: ModelRow[], parentIndex: number): number {
        let count = 0;
        table.rows.forEach((row, rowIndex) => {
            const applyValue = row.data.get("apply");
            if ("Y" === applyValue) {
                parentArray[parentIndex]["data"]["compare_items"][rowIndex]["apply"] = "Y";
                count++;
            } else {
                parentArray[parentIndex]["data"]["compare_items"][rowIndex]["apply"] = "N";
            }
        });
        return count;
    }

    public processOtherChargeCompareTable(table: Table, compareRows: ModelRow[]): number {
        let count: number = 0;
        table.rows.forEach(tableRow => {
            const chargeId = tableRow.data.get("compare_items").find((item: any) => item["target_fieldname"] === "charge_id")?.source_value;
            tableRow.data.set("charge_id", chargeId);
            compareRows.push(tableRow.data);
            if ("Y" == tableRow.data.get("apply")) {
                count++;
            }
        });
        return count;
    }

    public get buttonApply(): Button {
        return this._buttonApply;
    }
    public set buttonApply(value: Button) {
        this._buttonApply = value;
    }

    private readonly componentMappings = new Map<string, { checkBoxValue: string; labelValue: string; compareLabel: string }>([
        ["tableOrderChanges", { checkBoxValue: "checkboxApplyOrder", labelValue: "labelCheckedOrder", compareLabel: "labelApplyErrorOrder" }],
        ["tableStopAdd", { checkBoxValue: "checkboxAddStopApply", labelValue: "labelCheckedAddStop", compareLabel: "labelAddStopApply" }],
        ["tableStopRemove", { checkBoxValue: "checkboxRemoveStopApply", labelValue: "labelCheckedRemovedStops", compareLabel: "labelRemoveStopApply" }],
        ["tableStopComment", { checkBoxValue: "checkboxApplyStop", labelValue: "labelCheckedStop", compareLabel: "labelApplyStop" }],
        ["tableStopRefNo", { checkBoxValue: "checkboxApplyRefNo", labelValue: "labelCheckedRefNo", compareLabel: "labelApplyRefNo" }],
        ["tableMovementChanges", { checkBoxValue: "checkboxApplyMovement", labelValue: "labelCheckedMovement", compareLabel: "labelApplyErrorMovement" }]
    ]);

    private readonly defaultComponentMapping = {
        checkBoxValue: "checkboxApply",
        labelValue: "labelChecked",
        compareLabel: "labelApplyError"
    };

    private readonly standardTableIds = new Set([
        "tableChangeCompareItems", "tableOtherCharge", "tableCompareItems",
        "tableHandlingChangeItems", "tableHandlingRequirementAdd",
        "tableFreightGroupItemChangeItems", "tableFreightGroupItemAdd"
    ]);

    private getCorrectComponentValues(enteredTable: Table): { checkBoxValue: string, labelValue: string } {
        const tableId = enteredTable.id;

        if (this.componentMappings?.has(tableId)) {
            const mapping = this.componentMappings?.get(tableId);
            return { checkBoxValue: mapping?.checkBoxValue, labelValue: mapping?.labelValue };
        }

        if (this.standardTableIds?.has(tableId)) {
            return { checkBoxValue: this.defaultComponentMapping?.checkBoxValue, labelValue: this.defaultComponentMapping?.labelValue };
        }

        // Fallback to default mapping
        return { checkBoxValue: this.defaultComponentMapping?.checkBoxValue, labelValue: this.defaultComponentMapping?.labelValue };
    }

    setCompareHistoryFields() {
        this.isCompareHistory = true;
        this.buttonSelectAll.visible = false;
        this.buttonDeselectAll.visible = false;
    }

    private readonly groupTypeFilters = {
        stopChanges: "SF",
        newStops: "NS",
        removedStops: "OS",
        changedOtherCharges: "CC",
        newOrRemovedOtherCharges: ["NO", "OC"],
        newOrRemovedAccessorials: ["NA", "AR"],
        changedAccessorials: "AX",
        newOrRemovedFreightGroupItems: ["NF", "FR"],
        changedFreightGroupItems: "FX",
        orderChangeGroupItems: "OF",
        movementChangeGroupItems: "MC",
        newOrRemovedExtraPay: ["NE", "ER"],
        changedExtraPay: "EX"
    };

    setCompareVariables(apiData: any, mode: DataSourceMode) {
        this.compareData = apiData;
        const changeGroups = new ModelRow("lme/datafusion/edi-compare-group", true, apiData);
        this.dataRows = this._getArrayAndNull(changeGroups, "compare_groups");
        this.mode = mode;

        this.assignFilteredData();

        if (this.isInvoice) {
            this.isSelectAllVisible = this.setIsSelectAllVisible();
        }
    }

    private assignFilteredData() {
        this.stopChanges = this.filterDataByGroupType(this.groupTypeFilters.stopChanges);
        this.newStops = this.filterDataByGroupType(this.groupTypeFilters.newStops);
        this.removedStops = this.filterDataByGroupType(this.groupTypeFilters.removedStops);
        this.changedOtherCharges = this.filterDataByGroupType(this.groupTypeFilters.changedOtherCharges);
        this.newOrRemovedOtherCharges = this.filterDataByGroupTypes(this.groupTypeFilters.newOrRemovedOtherCharges);
        this.newOrRemovedAccessorials = this.filterDataByGroupTypes(this.groupTypeFilters.newOrRemovedAccessorials);
        this.changedAccessorials = this.filterDataByGroupType(this.groupTypeFilters.changedAccessorials);
        this.newOrRemovedFreightGroupItems = this.filterDataByGroupTypes(this.groupTypeFilters.newOrRemovedFreightGroupItems);
        this.changedFreightGroupItems = this.filterDataByGroupType(this.groupTypeFilters.changedFreightGroupItems);
        this.orderChangeGroupItems = this.filterDataByGroupType(this.groupTypeFilters.orderChangeGroupItems);
        this.movementChangeGroupItems = this.filterDataByGroupType(this.groupTypeFilters.movementChangeGroupItems);
        this.newOrRemovedExtraPay = this.filterDataByGroupTypes(this.groupTypeFilters.newOrRemovedExtraPay);
        this.changedExtraPay = this.filterDataByGroupType(this.groupTypeFilters.changedExtraPay);
    }

    private filterDataByGroupType(groupType: string): ModelRow[] {
        return this.dataRows?.filter(dataRow => dataRow["group_type"] === groupType) || [];
    }

    private filterDataByGroupTypes(groupTypes: string[]): ModelRow[] {
        return this.dataRows?.filter(dataRow => groupTypes.includes(dataRow["group_type"])) || [];
    }

    setCompareTables() {
        this.setOrderChanges(this.orderChangeGroupItems, this.mode);
        this.setNewStops(this.newStops, this.mode);
        this.setStopChanges(this.stopChanges, this.mode);
        this.setRemovedStops(this.removedStops, this.mode);
        this.setOtherCharges(this.mode);
        this.setHandlingRequirements(this.mode);
        this.setFreightGroupItems(this.mode);
        this.setMovementChanges(this.movementChangeGroupItems, this.mode);
        this.setExtraPay(this.mode);
    }

    setLayoutValues(selectAll: boolean) {
        this.layoutOtherChargeCompare?.findComponentById((comp: any) => {
            if (comp instanceof Table) {
                const ocTable: Table = comp;
                if (ocTable.id == "tableOCChanges") {
                    ocTable?.rows?.forEach(tableRow => {
                        tableRow?.findComponentById((comp: any) => {
                            if (comp instanceof OtherChargeChangeCompareItem) {
                                const ocChangeItem: OtherChargeChangeCompareItem = comp;
                                this.tableSetSelected(ocChangeItem.tableChangeCompareItems, selectAll);
                            }
                        })

                    })
                } else if (ocTable.id = "tableOtherCharge") {
                    this.tableSetSelected(ocTable, selectAll);
                }

            }
        });
        this.layoutHandlingRequirementChange?.findComponentById((comp: any) => {
            if (comp instanceof Table) {
                const handlingReqTable: Table = comp;
                if (handlingReqTable.id === "tableHandlingRequirementChange") {
                    handlingReqTable?.rows?.forEach(tableRow => {
                        tableRow?.findComponentById((comp: any) => {
                            if (comp instanceof HandlingRequirementsCompareChangeItem) {
                                const handlingRequirmentChangeItem: HandlingRequirementsCompareChangeItem = comp;
                                this.tableSetSelected(handlingRequirmentChangeItem.tableHandlingChangeItems, selectAll);
                            }
                        });
                    });

                } else if (handlingReqTable.id === "tableHandlingRequirementAdd") {
                    this.tableSetSelected(handlingReqTable, selectAll);
                }
            }
        });
        this.layoutFreightGroupItemChange?.findComponentById((comp: any) => {
            if (comp instanceof Table) {
                const freightGroupTable: Table = comp;
                if (freightGroupTable.id === "tableFreightGroupItemChange") {
                    freightGroupTable?.rows?.forEach(tableRow => {
                        tableRow?.findComponentById((comp: any) => {
                            if (comp instanceof FreightGroupItemCompareChangeItem) {
                                const freightGroupChangeItem: FreightGroupItemCompareChangeItem = comp;
                                this.tableSetSelected(freightGroupChangeItem.tableFreightGroupItemChangeItems, selectAll);
                            }
                        });
                    });

                } else if (freightGroupTable.id === "tableFreightGroupItemAdd") {
                    this.tableSetSelected(freightGroupTable, selectAll);
                }
            }
        });
        this.layoutExtraPayCompare?.findComponentById((comp: any) => {
            if (comp instanceof Table) {
                const extraPayTable: Table = comp;
                if (extraPayTable.id === "tableEPChanges") {
                    extraPayTable?.rows?.forEach(tableRow => {
                        tableRow?.findComponentById((comp: any) => {
                            if (comp instanceof ExtraPayChangeCompareItem) {
                                const extraPayChangeItem: ExtraPayChangeCompareItem = comp;
                                this.tableSetSelected(extraPayChangeItem.tableChangeCompareItems, selectAll);
                            }
                        });
                    });
                } else if (extraPayTable.id === "tableDriverExtraPay") {
                    this.tableSetSelected(extraPayTable, selectAll);
                }
            }
        });
    }

    public get isCompareHistory(): boolean {
        return this._isCompareHistory;
    }

    public set isCompareHistory(isCompareHistory: boolean) {
        this._isCompareHistory = isCompareHistory;
    }

    public get isInvoiceMovementUpdateAllowed(): boolean {
        return this._isInvoiceMovementUpdateAllowed;
    }

    public set isInvoiceMovementUpdateAllowed(value: boolean) {
        this._isInvoiceMovementUpdateAllowed = value;
    }

    public get isInvoiceOrderUpdateAllowed(): boolean {
        return this._isInvoiceOrderUpdateAllowed;
    }

    public set isInvoiceOrderUpdateAllowed(value: boolean) {
        this._isInvoiceOrderUpdateAllowed = value;
    }

    public get isInvoiceStopUpdateAllowed(): boolean {
        return this._isInvoiceStopUpdateAllowed;
    }

    public set isInvoiceStopUpdateAllowed(value: boolean) {
        this._isInvoiceStopUpdateAllowed = value;
    }

    public get isSelectAllVisible(): boolean {
        return this._isSelectAllVisible;
    }

    public set isSelectAllVisible(value: boolean) {
        this._isSelectAllVisible = value;
    }

    private setIsSelectAllVisible(): boolean {
        const compareItemArrays = [
            this.orderChangeGroupItems,
            this.stopChanges,
            this.newStops,
            this.removedStops,
            this.changedOtherCharges,
            this.newOrRemovedOtherCharges,
            this.newOrRemovedAccessorials,
            this.changedAccessorials,
            this.newOrRemovedFreightGroupItems,
            this.changedFreightGroupItems,
            this.movementChangeGroupItems,
            this.newOrRemovedExtraPay,
            this.changedExtraPay
        ];

        return compareItemArrays.some(array =>
            array && array.length > 0 &&
            array[0]["compare_items"]?.some((item: any) => item?.allowed)
        );
    }

    private setCheckboxVisibility(checkbox: Checkbox, labelChecked: Label, allowedCondition: () => boolean) {
        if (allowedCondition()) {
            checkbox.printable = false;
            checkbox.addClickListener(() => labelChecked.visible = checkbox.checked);
        } else {
            checkbox.visible = false;
        }
    }

    private checkInvoiceOrderUpdateAllowed(rows: any[]): boolean {
        return this.isInvoice && rows.some(row => row?.allowed);
    }
}
